/**
 * useAffiliateCookie
 * ------------------
 * Read `?ref=<CODE>` from the URL on any page. If present, persist the
 * code as a 90-day cookie `levanta_aff` so downstream pages (notably
 * /download-app) can surface a "Your friend gave you X% off" banner.
 *
 * Why a cookie and not localStorage:
 *   - Cookies survive a Safari/iOS share-sheet open-in-app flow that
 *     sometimes nukes localStorage.
 *   - Cookies are readable by server-rendered pages in the future
 *     (e.g. if we ever SSR a personalised download page).
 *
 * The hook also strips `?ref=…` from the visible URL (history.replaceState)
 * so the affiliate code doesn't linger in the address bar or get
 * re-shared accidentally.
 *
 * Usage: call once near the top of the React tree — e.g. in App.js.
 */
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const COOKIE_NAME = 'levanta_aff';
const COOKIE_DAYS = 90;
const CODE_RE = /^[A-Z0-9]{4,32}$/;

function writeCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  // SameSite=Lax so the cookie survives a same-site click from the
  // homepage to /download-app but still won't be sent cross-site on
  // third-party iframes.
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
}

export default function useAffiliateCookie() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('ref');
    if (!raw) return;

    const code = raw.trim().toUpperCase();
    if (!CODE_RE.test(code)) return;

    writeCookie(COOKIE_NAME, code, COOKIE_DAYS);

    // Strip `?ref=CODE` from the URL without triggering a navigation.
    // Preserves any other query params and the current path.
    params.delete('ref');
    const cleanedSearch = params.toString();
    const nextUrl =
      location.pathname + (cleanedSearch ? `?${cleanedSearch}` : '') + location.hash;
    navigate(nextUrl, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
}

export function readAffiliateCookie() {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + COOKIE_NAME + '=([^;]*)'),
  );
  if (!match) return null;
  try {
    const code = decodeURIComponent(match[1] || '').trim().toUpperCase();
    return CODE_RE.test(code) ? code : null;
  } catch {
    return null;
  }
}
